
import React, { useContext, useState, useEffect } from 'react';
import { AppContext } from '../App';
import { supabase, uploadFile } from '../supabase';

const Profile: React.FC = () => {
  const context = useContext(AppContext);
  
  const [isUpdating, setIsUpdating] = useState(false);
  const [isChangingPass, setIsChangingPass] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [avatarPreview, setAvatarPreview] = useState('');

  useEffect(() => {
    if (context?.currentUser) {
      setAvatarPreview(context.currentUser.avatarUrl);
    }
  }, [context?.currentUser]);

  if (!context || !context.currentUser) return null;
  const { currentUser, syncAllData, setCurrentUser, setProfiles } = context;

  const handleUpdateInfo = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsUpdating(true);
    setErrorMsg('');
    setSuccessMsg('');

    const formData = new FormData(e.currentTarget);
    const name = formData.get('name') as string;
    const avatarUrl = avatarPreview;

    try {
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ name, avatar_url: avatarUrl })
        .eq('id', currentUser.id);

      if (profileError) throw profileError;

      await syncAllData();
      setSuccessMsg('Dados atualizados com sucesso!');
      setTimeout(() => setSuccessMsg(''), 5000);
    } catch (err: any) {
      console.error('Erro ao atualizar perfil:', err);
      setErrorMsg(err.message || 'Erro inesperado ao salvar.');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUpdating(true);
    try {
      const publicUrl = await uploadFile(file);
      setAvatarPreview(publicUrl);
    } catch (err: any) {
      setErrorMsg('Erro ao fazer upload: ' + err.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsChangingPass(true);
    setErrorMsg('');
    setSuccessMsg('');

    const formData = new FormData(e.currentTarget);
    const newPass = formData.get('newPassword') as string;
    const confirmPass = formData.get('confirmPassword') as string;

    if (newPass.length < 8) {
      setErrorMsg('A senha deve ter no mínimo 8 caracteres.');
      setIsChangingPass(false);
      return;
    }

    if (newPass !== confirmPass) {
      setErrorMsg('As senhas não coincidem.');
      setIsChangingPass(false);
      return;
    }

    try {
      const { error: authError } = await supabase.auth.updateUser({ password: newPass });
      if (authError) throw authError;

      if (currentUser.isFirstLogin) {
        const { error: profileError } = await supabase
          .from('profiles')
          .update({ is_first_login: false })
          .eq('id', currentUser.id);
        if (profileError) throw profileError;
        setCurrentUser(prev => prev ? { ...prev, isFirstLogin: false } : prev);
        setProfiles(prev => prev.map(p => p.id === currentUser.id ? { ...p, isFirstLogin: false } : p));
      }

      setSuccessMsg('Senha alterada com sucesso!');
      (e.target as HTMLFormElement).reset();
      await syncAllData();
      setTimeout(() => setSuccessMsg(''), 5000);
    } catch (err: any) {
      setErrorMsg(err.message);
    } finally {
      setIsChangingPass(false);
    }
  };

  return (
    <div className="p-4 md:p-8 pb-32 md:pb-12 max-w-4xl mx-auto flex flex-col gap-8 animate-in fade-in duration-500 overflow-x-hidden">
      <header>
        <div className="flex items-center gap-4">
           <div className="size-12 rounded-2xl bg-primary text-white flex items-center justify-center">
             <span className="material-symbols-outlined text-2xl">person_pin</span>
           </div>
           <div>
              <h2 className="text-3xl md:text-4xl font-black text-text-main tracking-tight">Meus Dados</h2>
              <p className="text-text-secondary font-bold text-base md:text-lg mt-1">Gerencie sua identidade no ERP Magenta.</p>
           </div>
        </div>
      </header>

      {(successMsg || errorMsg) && (
        <div className={`p-6 rounded-[23px] border font-black text-[10px] uppercase tracking-widest text-center animate-in slide-in-from-top-4 duration-300 ${successMsg ? 'bg-green-50 border-green-100 text-green-600' : 'bg-red-50 border-red-100 text-red-600'}`}>
          {successMsg || errorMsg}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        <div className="lg:col-span-7 bg-white rounded-[23px] border border-gray-100 shadow-sm p-8 md:p-12">
          <form onSubmit={handleUpdateInfo} className="flex flex-col gap-10">
            <div className="flex flex-col sm:flex-row items-center gap-8">
              <div className="size-32 rounded-[32px] bg-primary-light border-2 border-dashed border-primary/20 flex items-center justify-center overflow-hidden shrink-0 shadow-inner relative group bg-white">
                {avatarPreview ? (
                  <img src={avatarPreview} className="w-full h-full object-cover" alt="Preview" />
                ) : (
                  <span className="material-symbols-outlined text-gray-300 text-4xl">person</span>
                )}
                <label className="absolute inset-0 bg-primary/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                  <span className="material-symbols-outlined text-white text-3xl">upload</span>
                  <input type="file" className="hidden" accept="image/*" onChange={handleAvatarUpload} />
                </label>
              </div>
              <div className="flex-1 w-full space-y-3">
                <label className="text-[10px] font-black uppercase tracking-widest text-text-secondary ml-1">URL Direta da Foto (Avatar)</label>
                <input 
                  type="text"
                  value={avatarPreview} 
                  onChange={(e) => setAvatarPreview(e.target.value)}
                  className="w-full h-14 px-6 rounded-2xl bg-gray-50 border-2 border-transparent focus:border-primary/20 focus:bg-white outline-none font-bold text-sm transition-all shadow-sm" 
                  placeholder="Link da sua foto..."
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6">
              <div className="flex flex-col gap-2">
                <label className="text-[10px] font-black uppercase text-text-secondary tracking-widest ml-1">Nome Completo</label>
                <input name="name" defaultValue={currentUser.name} required className="h-14 px-6 rounded-2xl bg-gray-50 border-2 border-transparent outline-none font-bold text-sm transition-all focus:bg-white focus:border-primary/20" />
              </div>
              <div className="flex flex-col gap-2 opacity-60">
                <label className="text-[10px] font-black uppercase text-text-secondary tracking-widest ml-1">Email de Login (Somente Leitura)</label>
                <input 
                  name="email" 
                  type="email" 
                  value={currentUser.email} 
                  disabled
                  className="h-14 px-6 rounded-2xl bg-gray-100 border-none outline-none font-bold text-sm cursor-not-allowed" 
                />
              </div>
            </div>

            <button type="submit" disabled={isUpdating} className="h-16 rounded-2xl bg-text-main text-white font-black uppercase tracking-widest shadow-xl hover:scale-[1.01] active:scale-95 transition-all disabled:opacity-50">
              {isUpdating ? 'Salvando...' : 'Salvar Alterações'}
            </button>
          </form>
        </div>

        <div className="lg:col-span-5 flex flex-col gap-8">
          <div className="bg-white rounded-[23px] border border-gray-100 shadow-sm p-8 md:p-10">
            <div className="flex items-center gap-4 mb-8">
              <div className="size-10 rounded-xl bg-orange-50 text-orange-500 flex items-center justify-center">
                <span className="material-symbols-outlined text-[20px]">shield</span>
              </div>
              <h3 className="text-xl font-black text-text-main tracking-tight">Alterar Senha</h3>
            </div>

            <form onSubmit={handleChangePassword} className="flex flex-col gap-6">
              <div className="flex flex-col gap-2">
                <label className="text-[10px] font-black uppercase text-text-secondary tracking-widest ml-1">Nova Senha</label>
                <input name="newPassword" type="password" required className="h-14 px-6 rounded-2xl bg-gray-50 border-2 border-transparent outline-none font-bold text-sm focus:bg-white focus:border-primary/20" placeholder="••••••••" />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-[10px] font-black uppercase text-text-secondary tracking-widest ml-1">Repita a Senha</label>
                <input name="confirmPassword" type="password" required className="h-14 px-6 rounded-2xl bg-gray-50 border-2 border-transparent outline-none font-bold text-sm focus:bg-white focus:border-primary/20" placeholder="••••••••" />
              </div>
              <button type="submit" disabled={isChangingPass} className="h-16 rounded-2xl bg-primary text-white font-black uppercase tracking-widest shadow-xl shadow-primary/20 hover:scale-[1.01] active:scale-95 transition-all disabled:opacity-50">
                {isChangingPass ? 'Processando...' : 'Atualizar Senha'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
